import * as React from 'react';
import styled, { withTheme } from 'styled-components';
import { rem } from 'polished';
import { useState } from 'react';
import { Range } from 'rc-slider';
import 'rc-slider/assets/index.css';
import { FlexRowCenterVertical, TextP } from '../../..';
import { DsiInput as Input } from '../General/Input';
import { __ } from 'react-i18n';
import { FilterType } from './CategoryFilter';

interface Props {
  theme: any;
  isOpen: boolean;
  filterData: {
    min: number;
    max: number;
    activeMin: string;
    activeMax: string;
    attribId?: number;
  };
  handleFilterChange: (
    type: FilterType,
    newData: { min: number; max: number; attribId?: number },
  ) => void;
  unit?: string;
  activeValues?: any;
}

function RangeFilter({
  theme,
  isOpen,
  filterData: { min, max, activeMin, activeMax, attribId },
  handleFilterChange,
  unit,
  activeValues,
}: Props) {
  if (!max || (!min && min !== 0)) {
    return null;
  }
  const correctMin = parseInt(Math.floor(min).toFixed(0), 10);
  const correctMax = parseInt(Math.ceil(max).toFixed(0), 10);
  const initialMin = activeMin
    ? parseInt(activeMin, 10).toFixed(0)
    : min.toFixed(0);
  const initialMax = activeMax
    ? parseInt(activeMax, 10).toFixed(0)
    : correctMax.toFixed(0);
  const [minValue, setMinValue] = useState(parseInt(initialMin, 10));
  const [maxValue, setMaxValue] = useState(parseInt(initialMax, 10));

  const [inputMin, setInputMin] = useState(initialMin);
  const [inputMax, setInputMax] = useState(initialMax);

  React.useEffect(() => {
    const correctInitialMin = activeMin
      ? parseInt(activeMin, 10).toFixed(0)
      : min.toFixed(0);
    const correctInitialMax = activeMax
      ? parseInt(activeMax, 10).toFixed(0)
      : correctMax.toFixed(0);

    setMinValue(parseInt(correctInitialMin, 10));
    setMaxValue(parseInt(correctInitialMax, 10));
    setInputMin(correctInitialMin);
    setInputMax(correctInitialMax);
  }, [activeMin, activeMax, min, correctMax]);

  const defaultValue = [min, correctMax];
  const sliderValue = [minValue, maxValue];

  const handleRangeChange = value => {
    console.log('range change: ', { value });
    setMinValue(value[0]);
    setMaxValue(value[1]);
    setInputMin(value[0]);
    setInputMax(value[1]);
  };

  const handleMinChange = e => {
    setInputMin(e.target.value);
  };

  const handleMaxChange = e => {
    console.log('max change');
    setInputMax(e.target.value);
  };

  const handleMinBlur = e => {
    let value = e.target.value;
    if (isNaN(value)) {
      value = initialMin;
    } else if (value < min) {
      value = min.toFixed(0);
    } else if (value > correctMax) {
      value = correctMax.toFixed(0);
    } else {
      value = parseInt(value, 10).toFixed(0);
    }

    if (value !== initialMin) {
      setMinValue(value);
      setInputMin(value);
      handleFilterChange(FilterType.RANGE, {
        min: parseInt(value, 10),
        max: maxValue,
        attribId,
      });
    } else {
      setInputMin(value);
    }
  };

  const handleMaxBlur = e => {
    console.log('max blur');
    let value = e.target.value;
    if (isNaN(value)) {
      value = initialMax;
    } else if (value > max) {
      value = correctMax.toFixed(0);
    } else if (value < min) {
      value = min.toFixed(0);
    } else {
      value = parseInt(value, 10).toFixed(0);
    }

    if (value !== initialMax) {
      setInputMax(value);
      setMaxValue(value);
      handleFilterChange(FilterType.RANGE, {
        min: minValue,
        max: parseInt(value, 10),
        attribId,
      });
    } else {
      setInputMax(value);
    }
  };

  const handleAfterRangeChange = () => {
    if (
      activeMin !== minValue.toFixed(0) ||
      activeMax !== maxValue.toFixed(0)
    ) {
      handleFilterChange(FilterType.RANGE, {
        min: minValue,
        max: maxValue,
        attribId,
      });
      setInputMin(minValue.toFixed(0));
      setInputMax(maxValue.toFixed(0));
    }
  };

  return (
    <>
      {isOpen && (
        <PriceFilterDropdown>
          <Range
            className="rc-slider-custom"
            min={correctMin}
            max={correctMax}
            allowCross={false}
            value={sliderValue}
            defaultValue={defaultValue}
            onChange={handleRangeChange}
            onAfterChange={handleAfterRangeChange}
            handleStyle={[
              // first track style
              {
                backgroundColor: theme.categoryFilter.trackBgColor,
                border: theme.categoryFilter.trackBorder,
                borderColor: theme.categoryFilter.trackBorderColor,
                width: rem(16),
                height: rem(16),
              },
              // second track style
              {
                backgroundColor: theme.categoryFilter.trackBgColor,
                border: theme.categoryFilter.trackBorder,
                borderColor: theme.categoryFilter.trackBorderColor,
                width: rem(16),
                height: rem(16),
              },
            ]}
            trackStyle={[
              { backgroundColor: theme.categoryFilter.sliderTrackColor },
            ]}
            railStyle={{
              backgroundColor: theme.categoryFilter.sliderRailColor,
            }}
          />
          <InputRow>
            <PriceLimitInput
              type="number"
              value={inputMin}
              onChange={handleMinChange}
              onBlur={handleMinBlur}
            />
            <TextP style={{ marginRight: 16, marginLeft: 16 }}>
              {__('do')}
            </TextP>
            <PriceLimitInput
              type="number"
              value={inputMax}
              onChange={handleMaxChange}
              onBlur={handleMaxBlur}
            />
            {unit ? <TextP style={{ marginLeft: 16 }}>{unit}</TextP> : null}
          </InputRow>
        </PriceFilterDropdown>
      )}
    </>
  );
}

const FilterDropdown = styled.div`
  margin-top: ${rem(32)};
`;

const PriceFilterDropdown = styled(FilterDropdown)``;

const InputRow = styled(FlexRowCenterVertical)`
  margin-top: ${rem(32)};
`;

const PriceLimitInput = styled(Input)`
  box-shadow: 0 ${rem(1)} ${rem(4)} #00000014;
  border: ${rem(1)} solid #cccccc;
  border-radius: ${rem(4)};
  max-width: ${rem(80)};
  border-radius: ${rem(2)};
  padding: 0 ${rem(16)};
  text-align: center;

  /* Chrome, Safari, Edge, Opera */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  &[type='number'] {
    -moz-appearance: textfield;
  }
`;

const WithTheme: any = withTheme(RangeFilter);
export { WithTheme as RangeFilter };
